import { useCallback, useState } from 'react';
import { subscriptionGateway } from '../index';
import type { BillingHistoryKind } from '../billingTypes';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { EmptyState } from '../../../shared/EmptyState';
import { RetryableError } from '../../../shared/RetryableError';
import { useBilling } from './BillingContext';
import { Pager } from './InvoicesSection';
import { formatCents, formatUtcDate } from './money';
import { useResource } from './useResource';

const PAGE_SIZE = 10;

const KIND: Record<BillingHistoryKind, { label: string; glyph: string }> = {
  Invoice: { label: 'Invoice', glyph: '🧾' },
  Payment: { label: 'Payment', glyph: '✓' },
  Refund: { label: 'Refund', glyph: '↩' },
  PlanChange: { label: 'Plan change', glyph: '⇄' },
  TrialStarted: { label: 'Trial started', glyph: '▶' },
  PaymentFailed: { label: 'Payment failed', glyph: '⚠' },
  Cancellation: { label: 'Cancellation', glyph: '✕' },
};

/** Billing history timeline: invoices, payments, refunds, plan changes, trial start, failures and cancellations, newest
 * first, exactly as the server returned them. Each entry is labelled in words; the glyph is decoration. */
export function HistorySection() {
  const { refreshToken } = useBilling();
  const [page, setPage] = useState(1);
  const load = useCallback(
    (signal: AbortSignal) => subscriptionGateway.listBillingHistory({ page, pageSize: PAGE_SIZE }, signal),
    [page, refreshToken], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const { state, reload } = useResource(load, 'Could not load billing history.');

  return (
    <section aria-labelledby="billing-history-heading" className="billing-section" data-testid="billing-history">
      <h3 id="billing-history-heading">Billing history</h3>

      {state.status === 'loading' && <p className="muted" role="status">Loading billing history…</p>}
      {state.status === 'denied' && <AccessDeniedNotice requiredPermissionLabel="Billing: View" />}
      {state.status === 'unavailable' && <p className="muted">Billing is not available in this environment, so there is no history to show.</p>}
      {state.status === 'error' && <RetryableError message={state.message} onRetry={reload} />}

      {state.status === 'ready' && state.data.items.length === 0 && (
        <EmptyState message="No billing activity yet. Invoices, payments, refunds and plan changes will appear here." />
      )}

      {state.status === 'ready' && state.data.items.length > 0 && (
        <>
          <ol className="billing-timeline" aria-label="Billing history">
            {state.data.items.map((item, index) => {
              const kind = KIND[item.kind] ?? { label: item.kind, glyph: '•' };
              return (
                <li key={`${item.occurredAtUtc}-${item.kind}-${index}`} className="billing-timeline__item">
                  <span className="billing-timeline__glyph" aria-hidden="true">{kind.glyph}</span>
                  <div>
                    <p className="billing-timeline__head">
                      <strong>{kind.label}</strong>
                      <span className="muted"> · <time dateTime={item.occurredAtUtc}>{formatUtcDate(item.occurredAtUtc)}</time></span>
                    </p>
                    <p>{item.summary}{item.amountCents !== null && <> <span className="billing-timeline__amount">{formatCents(item.amountCents)}</span></>}</p>
                    {item.reference && <p className="muted">Reference: {item.reference}</p>}
                  </div>
                </li>
              );
            })}
          </ol>
          <Pager page={state.data.page} pageSize={state.data.pageSize} totalCount={state.data.totalCount} onPage={setPage} noun="billing history" />
        </>
      )}
    </section>
  );
}
